// Desert Base — arid outpost with palm trees, sand, sandbags and comm-tower.
// Rebuild focus: sand ground, camo tents, palm trees, corrugated barracks,
// sandbag bunkers, watchtowers, radio dish, jeep silhouette.
import EnvironmentAtmosphere from "@/game/systems/Environment";
import {
    ConcreteWall,
    Crate,
    Sandbags,
    Palm,
    Building,
    Barrel,
    Sign,
    Ground,
    StreetLamp,
    Fence,
} from "@/game/systems/props";
import { MAT } from "@/game/systems/materials";

export const DESERT_BASE_WALLS = [
    // Perimeter
    { min: [-25, 0, -25], max: [-24, 4, 25] },
    { min: [24, 0, -25], max: [25, 4, 25] },
    { min: [-25, 0, -25], max: [25, 4, -24] },
    { min: [-25, 0, 24], max: [25, 4, 25] },
    // Central elevated platform
    { min: [-3, 0, -3], max: [3, 1.2, 3] },
    // Four tall pillars around center
    { min: [-9, 0, -9], max: [-7, 4, -7] },
    { min: [7, 0, -9], max: [9, 4, -7] },
    { min: [-9, 0, 7], max: [-7, 4, 9] },
    { min: [7, 0, 7], max: [9, 4, 9] },
    // Watchtower bases
    { min: [-22, 0, -22], max: [-19, 4, -19] },
    { min: [19, 0, -22], max: [22, 4, -19] },
    { min: [-22, 0, 19], max: [-19, 4, 22] },
    { min: [19, 0, 19], max: [22, 4, 22] },
    // Sandbag walls
    { min: [-16, 0, -4], max: [-13, 1.1, -2] },
    { min: [13, 0, -4], max: [16, 1.1, -2] },
    { min: [-16, 0, 2], max: [-13, 1.1, 4] },
    { min: [13, 0, 2], max: [16, 1.1, 4] },
    { min: [-4, 0, -16], max: [-2, 1.1, -13] },
    { min: [2, 0, -16], max: [4, 1.1, -13] },
    { min: [-4, 0, 13], max: [-2, 1.1, 16] },
    { min: [2, 0, 13], max: [4, 1.1, 16] },
];

export const DESERT_BASE_SPAWNS = {
    player: { position: [0, 1.7, 20], yaw: Math.PI },
    enemies: [[0, 1.7, -20], [-12, 1.7, -18], [12, 1.7, -18], [-20, 1.7, -12], [20, 1.7, -12]],
    allies: [[-4, 1.7, 20], [4, 1.7, 20], [-12, 1.7, 18], [12, 1.7, 18]],
};

export const DESERT_BASE_WAYPOINTS = [
    [0, 0, -14], [-14, 0, -14], [14, 0, -14],
    [-20, 0, -6], [20, 0, -6], [-6, 0, -6], [6, 0, 6], [0, 0, 0],
    [-14, 0, 14], [14, 0, 14], [-20, 0, 6], [20, 0, 6],
];

export const DESERT_BASE_META = {
    id: "desert_base",
    name: "Desert Base",
    walls: DESERT_BASE_WALLS,
    spawns: DESERT_BASE_SPAWNS,
    waypoints: DESERT_BASE_WAYPOINTS,
    bounds: { minX: -24, maxX: 24, minZ: -24, maxZ: 24 },
    preview: {
        environment: "DESERT OUTPOST",
        recommendedPlayers: "6 – 10",
        difficulty: "ADVANCED",
        palette: ["#c8b075", "#6b3a26", "#7a6a45"],
        tagline: "Open sand, long sight-lines and sandbag bunkers.",
    },
};

/** Watchtower with wooden legs + camo tarp roof */
function Watchtower({ position, shadows }) {
    return (
        <group position={position}>
            {/* Four legs */}
            {[[-1.4, -1.4], [1.4, -1.4], [-1.4, 1.4], [1.4, 1.4]].map(([x, z], i) => (
                <mesh key={i} position={[x, 2, z]} castShadow={shadows} material={MAT.wood()}>
                    <boxGeometry args={[0.25, 4, 0.25]} />
                </mesh>
            ))}
            {/* Base sandbags */}
            <Sandbags position={[0, 0, 1.8]} length={3} rows={2} shadows={shadows} />
            <Sandbags position={[0, 0, -1.8]} length={3} rows={2} shadows={shadows} />
            {/* Deck */}
            <mesh position={[0, 4.2, 0]} castShadow={shadows} material={MAT.woodLight()}>
                <boxGeometry args={[3.2, 0.2, 3.2]} />
            </mesh>
            {/* Canvas roof */}
            <mesh position={[0, 5.6, 0]} rotation={[0, Math.PI / 4, 0]} material={MAT.canvas()}>
                <coneGeometry args={[2.6, 1.6, 4]} />
            </mesh>
            {/* Warning light */}
            <mesh position={[0, 4.6, 0]} material={MAT.lightRed()}>
                <boxGeometry args={[0.2, 0.1, 0.2]} />
            </mesh>
            <pointLight position={[0, 4.5, 0]} intensity={0.7} color="#ff5040" distance={12} />
        </group>
    );
}

export default function DesertBase({ shadows, timeOfDay = "sunset", weather = "sunny" }) {
    return (
        <group>
            <EnvironmentAtmosphere timeOfDay={timeOfDay} weather={weather} shadows={shadows} />

            {/* Sand ground */}
            <Ground material="sand" size={140} />
            {/* Dirt path down the middle */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]} material={MAT.dirt()}>
                <planeGeometry args={[8, 50]} />
            </mesh>

            {/* Perimeter concrete/sandbag walls */}
            <ConcreteWall position={[-24.5, 2, 0]} size={[1, 4, 50]} color="concreteLight" stripe={false} shadows={shadows} />
            <ConcreteWall position={[24.5, 2, 0]} size={[1, 4, 50]} color="concreteLight" stripe={false} shadows={shadows} />
            <ConcreteWall position={[0, 2, -24.5]} size={[50, 4, 1]} color="concreteLight" stripe={false} shadows={shadows} />
            <ConcreteWall position={[0, 2, 24.5]} size={[50, 4, 1]} color="concreteLight" stripe={false} shadows={shadows} />

            {/* Central raised platform (concrete pad) */}
            <mesh position={[0, 0.6, 0]} castShadow={shadows} receiveShadow material={MAT.concrete()}>
                <boxGeometry args={[6, 1.2, 6]} />
            </mesh>
            {/* Radio dish antenna on the pad */}
            <group position={[0, 1.3, 0]}>
                <mesh position={[0, 1.5, 0]} material={MAT.metalDark()}>
                    <boxGeometry args={[0.15, 3, 0.15]} />
                </mesh>
                {/* Dish */}
                <mesh position={[0, 3.0, 0]} rotation={[Math.PI / 2, 0, 0]} material={MAT.metalDark()}>
                    <coneGeometry args={[0.9, 0.35, 12, 1, true]} />
                </mesh>
                <mesh position={[0, 2.7, 0]} material={MAT.lightRed()}>
                    <boxGeometry args={[0.15, 0.15, 0.15]} />
                </mesh>
            </group>

            {/* Four camo tents around the pad */}
            {[[-9, -9], [9, -9], [-9, 9], [9, 9]].map(([x, z], i) => (
                <group key={i} position={[x, 0, z]}>
                    {/* Corrugated barracks structure */}
                    <Building
                        position={[0, 2, 0]}
                        size={[3.5, 4, 3.5]}
                        color="concreteDark"
                        withWindows={false}
                        shadows={shadows}
                    />
                    {/* Camo canopy */}
                    <mesh position={[0, 4.4, 0]} material={MAT.canvas()}>
                        <coneGeometry args={[2.8, 1.2, 4]} />
                    </mesh>
                </group>
            ))}

            {/* Watchtowers at corners */}
            <Watchtower position={[-20.5, 0, -20.5]} shadows={shadows} />
            <Watchtower position={[20.5, 0, -20.5]} shadows={shadows} />
            <Watchtower position={[-20.5, 0, 20.5]} shadows={shadows} />
            <Watchtower position={[20.5, 0, 20.5]} shadows={shadows} />

            {/* Sandbag walls along the approach lanes */}
            <Sandbags position={[-14.5, 0, -3]} length={3} shadows={shadows} />
            <Sandbags position={[14.5, 0, -3]} length={3} shadows={shadows} />
            <Sandbags position={[-14.5, 0, 3]} length={3} shadows={shadows} />
            <Sandbags position={[14.5, 0, 3]} length={3} shadows={shadows} />
            <Sandbags position={[-3, 0, -14.5]} rotation={[0, Math.PI / 2, 0]} length={3} shadows={shadows} />
            <Sandbags position={[3, 0, -14.5]} rotation={[0, Math.PI / 2, 0]} length={3} shadows={shadows} />
            <Sandbags position={[-3, 0, 14.5]} rotation={[0, Math.PI / 2, 0]} length={3} shadows={shadows} />
            <Sandbags position={[3, 0, 14.5]} rotation={[0, Math.PI / 2, 0]} length={3} shadows={shadows} />

            {/* Palm trees scattered outside pillars */}
            <Palm position={[-16, 0, 0]} scale={1.1} shadows={shadows} />
            <Palm position={[16, 0, 0]} scale={1.0} shadows={shadows} />
            <Palm position={[-6, 0, -20]} scale={0.9} shadows={shadows} />
            <Palm position={[6, 0, 20]} scale={1.1} shadows={shadows} />
            <Palm position={[0, 0, -22]} scale={0.85} shadows={shadows} />
            <Palm position={[0, 0, 22]} scale={0.95} shadows={shadows} />

            {/* Jeep silhouette */}
            <group position={[-12, 0.8, -3]}>
                <mesh material={MAT.canvas()}>
                    <boxGeometry args={[3.2, 1.4, 1.8]} />
                </mesh>
                <mesh position={[0.6, 0.9, 0]} material={MAT.canvas()}>
                    <boxGeometry args={[1.4, 0.8, 1.6]} />
                </mesh>
                {[[-1.2, -0.75, 0.8], [1.2, -0.75, 0.8], [-1.2, -0.75, -0.8], [1.2, -0.75, -0.8]].map(([x, y, z], i) => (
                    <mesh key={i} position={[x, y, z]} rotation={[0, 0, Math.PI / 2]} material={MAT.metalDark()}>
                        <cylinderGeometry args={[0.35, 0.35, 0.3, 10]} />
                    </mesh>
                ))}
            </group>

            {/* Oil drum stack */}
            <Barrel position={[12, 0.55, -4]} color="metalRust" shadows={shadows} />
            <Barrel position={[12.8, 0.55, -4]} color="metalRed" shadows={shadows} />
            <Barrel position={[12, 0.55, -3.2]} color="metalYellow" shadows={shadows} />

            {/* Crates near landing pad */}
            <Crate position={[-4, 0.7, 8]} shadows={shadows} />
            <Crate position={[4, 0.7, 8]} rotation={[0, 0.3, 0]} shadows={shadows} />
            <Crate position={[-4, 0.7, -8]} shadows={shadows} />

            {/* Directional signs */}
            <Sign position={[-10, 0, 20]} color="signWhite" />
            <Sign position={[10, 0, 20]} rotation={[0, -0.3, 0]} color="signWhite" />

            {/* Fence sections between palms */}
            <Fence position={[-16, 0, 6]} rotation={[0, Math.PI / 2, 0]} length={4} shadows={shadows} />
            <Fence position={[16, 0, -6]} rotation={[0, Math.PI / 2, 0]} length={4} shadows={shadows} />

            {/* Perimeter floods */}
            <StreetLamp position={[-22, 0, 0]} warm={false} shadows={shadows} />
            <StreetLamp position={[22, 0, 0]} warm={false} shadows={shadows} />

            {/* Distant dune ridge (just a big low sand hill) */}
            <mesh position={[0, -1, -60]} material={MAT.sand()}>
                <sphereGeometry args={[40, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            </mesh>
            <mesh position={[0, -1, 60]} material={MAT.sand()}>
                <sphereGeometry args={[45, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            </mesh>
        </group>
    );
}
